import { useRedirectQsParam } from "../useRedirectQsParam";
import { track } from "../../core/App/track";
import { useFormik } from 'formik';
import { useLoginMutation, useMfaLoginMutation } from 'legoland-sdk/dist/experimental';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Box, Button, FormGroup, InlineText, Input, Text, Tooltip, tombac, useToasts } from 'tombac';
import { ErrorIcon, ForwardIcon } from 'tombac-icons';
import isEmail from 'validator/lib/isEmail';
import { object, string } from 'yup';
import { LoginOtpInput } from "./LoginOtpPopup";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Actions = styled.div.withConfig({
  displayName: "LoginForm__Actions"
})(["align-items:stretch;display:flex;flex-direction:column;margin-top:", ";@media (min-width:768px){display:block;}"], tombac.space(2));
const Form = styled.form.withConfig({
  displayName: "LoginForm__Form"
})(["padding:", " 0;@media (min-width:768px){margin:", " 0;width:480px;}@media (min-width:1280px){width:540px;}.LoginForm__password-input > [class^='FormGroup__CaptionArea']{grid-area:toolbar;}"], tombac.space(3), tombac.space(2));
const loginValidationSchema = object({
  email: string().ensure().trim().required('This field is required.').test({
    name: 'email',
    message: 'This field must contain a valid email address.',
    test: value => value !== '' && isEmail(value)
  }),
  password: string().required('This field is required.')
});
const otpValidationSchema = object({
  otp: string().required('This field is required.').length(6, 'This field must contain exactly 6 digits.')
});
const mfaDataSchema = object({
  mfaToken: string().required(),
  expiresAt: string().required()
});
const incorrectCodeMessage = 'The code you entered is incorrect.';

/**
 * The login form component.
 *
 * This component is responsible for rendering the login form and handling the
 * login process.
 *
 * The login process is as follows:
 *
 * 1. The user enters their email and password.
 * 2. The user submits the form.
 * 3. If the credentials are incorrect, an error message is displayed.
 * 4. If the credentials are correct:
 *
 *    1. If the user doesn't have MFA enabled, they are logged in.
 *    2. If the user has MFA enabled, they are prompted to enter the one-time password
 *          (OTP).
 *    3. If the OTP is incorrect, an error message is displayed.
 *    4. If the OTP is correct, the user is logged in.
 */
export const LoginForm = () => {
  const {
    addToast
  } = useToasts();

  // The MFA data is only set when the user has MFA enabled.
  // The data comes with the 403 response on successful login with email and password.
  const [mfaData, setMfaData] = useState(null);

  /* Login with email and password. */

  const {
    mutateAsync: login
  } = useLoginMutation();
  const [validateOnChange, setValidateOnChange] = useState(false);
  const {
    url: redirectUrl,
    redirect
  } = useRedirectQsParam();
  const handleLoginSubmit = async ({
    email,
    password
  }) => {
    try {
      await login({
        username: email,
        password
      });
      handleSuccessfulLogin();
    } catch (error) {
      await handleLoginError(error);
    }
  };
  const handleSuccessfulLogin = () => {
    track('account_login', {
      category: 'user',
      subcategory: 'login',
      specificProps: {
        account_type: 'move'
      }
    });
    if (redirectUrl) {
      redirect();
    }
  };
  async function handleMfaData(response) {
    const unsafeData = await response.json();
    const data = mfaDataSchema.validateSync(unsafeData);
    setMfaData(data);
  }
  const handleLoginError = async loginError => {
    try {
      if (!(loginError instanceof Response)) throw loginError;
      if (loginError.status === 401) {
        loginFormik.setFieldError('email', 'Invalid username or password.');
      } else if (loginError.status === 403) {
        // The user has MFA enabled, so we need to show the OTP form.
        await handleMfaData(loginError);
      } else {
        throw loginError;
      }
    } catch (error) {
      console.error(loginError);
      loginFormik.setFieldError('email', 'An unexpected error occurred. Please try again later.');
    }
  };
  const loginFormik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    onSubmit: handleLoginSubmit,
    validateOnBlur: false,
    validateOnChange,
    validationSchema: loginValidationSchema
  });
  useEffect(() => {
    setValidateOnChange(loginFormik.submitCount > 0);
  }, [loginFormik.submitCount > 0]);
  const getLoginFormGroupProps = name => ({
    variant: loginFormik.touched[name] && loginFormik.errors[name] ? 'danger' : 'default'
  });
  const getLoginInputProps = name => ({
    append: loginFormik.touched[name] && loginFormik.errors[name] ? /*#__PURE__*/_jsx(Tooltip, {
      className: "LoginForm__error-message",
      content: loginFormik.errors[name],
      "data-field-name": name,
      isOpen: true,
      placement: "top",
      size: "s",
      variant: "danger",
      children: ({
        ref
      }) => /*#__PURE__*/_jsx("span", {
        ref: ref,
        children: /*#__PURE__*/_jsx(ErrorIcon, {
          fixedWidth: false,
          style: {
            display: 'block'
          }
        })
      })
    }) : null,
    invalid: Boolean(loginFormik.touched[name] && loginFormik.errors[name]),
    ...loginFormik.getFieldProps(name)
  });

  /* Login with OTP. */

  const {
    mutateAsync: mfaLogin
  } = useMfaLoginMutation();
  const otpInputRef = useRef(null);
  const handleOtpSubmit = async ({
    otp
  }) => {
    try {
      // The blur forces the user to click on the OTP input again
      // if they want to enter another code after a failed attempt.
      // The click will clear the field in `handleOtpInputClick`.
      otpInputRef.current?.blur();
      await mfaLogin({
        mfaToken: mfaData.mfaToken,
        otp
      });
      handleSuccessfulLogin();
    } catch (error) {
      handleOtpError(error);
    }
  };
  const handleOtpError = otpError => {
    if (otpError instanceof Response && otpError.status === 401) {
      otpFormik.setFieldError('otp', incorrectCodeMessage);
    } else {
      console.error(otpError);
      otpFormik.setFieldError('otp', 'An unexpected error occurred. Please try again later.');
    }
  };
  const handleOtpInputClick = () => {
    if (otpFormik.errors.otp?.includes(incorrectCodeMessage)) {
      // If the user clicks on the OTP input after entering an incorrect code,
      // we should clear the field and the error message, so they can try again.
      otpFormik.resetForm();
    }
  };
  const otpFormik = useFormik({
    initialValues: {
      otp: ''
    },
    onSubmit: handleOtpSubmit,
    validationSchema: otpValidationSchema
  });

  /* Resend OTP. */

  const [isResending, setIsResending] = useState(false);
  const handleResendClick = async () => {
    setIsResending(true);
    try {
      await login({
        username: loginFormik.values.email,
        password: loginFormik.values.password
      });
      // If somehow the user has already logged in, finish the process.
      // Otherwise, the "happy path" will be handled by the error handler (403 is expected).
      handleSuccessfulLogin();
    } catch (error) {
      if (error instanceof Response && error.status === 403) {
        // The new token has been sent, so we need to reset the form.
        otpFormik.resetForm();
        addToast("We've sent you a new code.", 'success');
        await handleMfaData(error);
        return;
      }
      handleResendError(error);
    } finally {
      setIsResending(false);
    }
  };
  const handleResendError = resendError => {
    if (!(resendError instanceof Response)) throw resendError;
    if (resendError.status === 401) {
      // The chance of this happening is very low, but it's better to refresh the page
      // and start the process from the beginning, in case the credentials have changed.
      window.location.reload();
    } else {
      console.error(resendError);
      otpFormik.setFieldError('otp', 'An unexpected error occurred. Please try again later.');
    }
  };
  return /*#__PURE__*/_jsx(Form, {
    onSubmit: mfaData ? otpFormik.handleSubmit : loginFormik.handleSubmit,
    "data-sentry-element": "Form",
    "data-sentry-component": "LoginForm",
    "data-sentry-source-file": "LoginForm.tsx",
    children: /*#__PURE__*/_jsxs(Box, {
      $display: "grid",
      $gap: "3sp",
      $gridAutoFlow: "row",
      $gridAutoRows: "max-content",
      $justifyContent: "stretch",
      "data-sentry-element": "Box",
      "data-sentry-source-file": "LoginForm.tsx",
      "data-sentry-element": "Box",
      children: [mfaData ? /*#__PURE__*/_jsx(FormGroup, {
        $maxWidth: "max-content",
        error: otpFormik.touched.otp && otpFormik.errors.otp,
        hint: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(InlineText, {
            fontSize: 12,
            variant: "secondary",
            children: "Didn't receive the code?"
          }), ' ', isResending ? /*#__PURE__*/_jsx(Text, {
            $display: "inline-block",
            fontSize: 12,
            children: "Resending..."
          }) : /*#__PURE__*/_jsx(Text, {
            $display: "inline-block",
            fontSize: 12,
            as: "button",
            onClick: handleResendClick,
            variant: "interactive",
            children: "Resend"
          })]
        }),
        label: "Enter the 6-digit code we sent to your email",
        htmlFor: "otp-input",
        variant: otpFormik.touched.otp && otpFormik.errors.otp ? 'danger' : 'default',
        children: /*#__PURE__*/_jsx(LoginOtpInput, {
          autoFocus: true,
          id: "otp-input",
          ...otpFormik.getFieldProps('otp'),
          onChange: value => otpFormik.setFieldValue('otp', value),
          onClick: handleOtpInputClick,
          onComplete: otpFormik.submitForm,
          ref: otpInputRef
        })
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormGroup, {
          htmlFor: "email-input",
          label: "Email",
          ...getLoginFormGroupProps('email'),
          children: /*#__PURE__*/_jsx(Input, {
            autoFocus: !loginFormik.touched.email,
            id: "email-input",
            ...getLoginInputProps('email')
          })
        }), /*#__PURE__*/_jsx(FormGroup, {
          caption: /*#__PURE__*/_jsx(Text, {
            as: Link,
            to: "/login/forgot-password",
            variant: "interactive",
            children: "Forgot password?"
          }),
          className: "LoginForm__password-input",
          htmlFor: "password-input",
          label: "Password",
          ...getLoginFormGroupProps('password'),
          children: /*#__PURE__*/_jsx(Input, {
            id: "password-input",
            type: "password",
            ...getLoginInputProps('password')
          })
        })]
      }), /*#__PURE__*/_jsx(Actions, {
        "data-sentry-element": "Actions",
        "data-sentry-source-file": "LoginForm.tsx",
        "data-sentry-element": "Actions",
        children: /*#__PURE__*/_jsx(Button, {
          append: /*#__PURE__*/_jsx(ForwardIcon, {}),
          appendedHoverEffect: "anim-forward",
          busy: loginFormik.isSubmitting || otpFormik.isSubmitting,
          id: "sign-in-button",
          type: "submit",
          variant: "primary",
          "data-sentry-element": "Button",
          "data-sentry-source-file": "LoginForm.tsx",
          "data-sentry-element": "Button",
          children: "Log in"
        })
      })]
    })
  });
};